import React from "react";
import "../Styles/Pricing.scss";
import {
  User,
  Users,
  Send,
  Database,
  Check,
  Star,
  Sparkles,
  Globe,
  FileText,
} from "lucide-react";

const masterPlansData = [
  {
    id: "advantage",
    name: "ADVANTAGE",
    price: 399,
    period: "month",
    conversations: "2,000 conversations / month",
    popular: false,
    users: "5 users",
    agents: "3 live agents",
    broadcasts: "10,000 broadcasts / month",
    storage: "75 GB storage",
    languages: "3 agent languages",
    features: [
      "Multimodal: Text, Image, Audio",
      "inFlow Copilot Assistant",
      "WhatsApp Calling (+ Add-on)",
      "Basic AI Reasoning Models",
      "Manual & Connector Knowledge Base",
      "90-Day Conversation Retention",
    ],
  },
  {
    id: "premium",
    name: "PREMIUM",
    price: 599,
    period: "month",
    conversations: "3,000 conversations / month",
    popular: true,
    users: "10 users",
    agents: "5 live agents",
    broadcasts: "25,000 broadcasts / month",
    storage: "100 GB storage",
    languages: "5 agent languages",
    features: [
      "Multimodal: Text, Image, Audio",
      "inFlow Copilot Assistant",
      "WhatsApp Suite: Basic, Catalog, Flows",
      "WhatsApp Voice Calling Included",
      "Basic & Premium AI Models",
      "Manual & Connector Knowledge Base",
      "90-Day Conversation Retention",
    ],
  },
  {
    id: "ultimate",
    name: "ULTIMATE",
    price: 899,
    period: "month",
    conversations: "5,000 conversations / month",
    popular: false,
    users: "Unlimited users",
    agents: "10 live agents",
    broadcasts: "25,000 broadcasts / month",
    storage: "250 GB storage",
    languages: "10 agent languages",
    features: [
      "Everything in Premium, plus:",
      "Unlimited Team & Admin Users",
      "10 Live Agent Handoff Seats",
      "Enterprise Single Sign-On (SSO)",
      "Dedicated Enterprise SLA Support",
    ],
  },
];


const docsAIPlansData = [
  {
    id: "advantage",
    name: "ADVANTAGE",
    price: 399,
    period: "month",
    popular: false,
    users: "5 users",
    agents: "5 agents",
    languages: "3 languages",
    storage: "75 GB storage",
    reviewers: "Unlimited reviewers",
    api: "API Included",
    features: [
      "5 Autonomous Document Agents",
      "3 Supported Document Languages",
      "75 GB Cloud Storage",
      "5 Team Users & Unlimited Reviewers",
      "Enterprise REST API Access",
      "Premium Reasoning Models",
    ],
  },
  {
    id: "premium",
    name: "PREMIUM",
    price: 599,
    period: "month",
    popular: true,
    users: "5 users",
    agents: "5 agents",
    languages: "5 languages",
    storage: "100 GB storage",
    reviewers: "Unlimited reviewers",
    api: "API Included",
    features: [
      "5 Autonomous Document Agents",
      "5 Supported Document Languages",
      "100 GB Cloud Storage",
      "5 Team Users & Unlimited Reviewers",
      "Enterprise REST API Access",
      "Premium Reasoning Models",
    ],
  },
  {
    id: "ultimate",
    name: "ULTIMATE",
    price: 899,
    period: "month",
    popular: false,
    users: "5 users",
    agents: "Unlimited agents",
    languages: "10 languages",
    storage: "250 GB storage",
    reviewers: "Unlimited reviewers",
    api: "API Included",
    features: [
      "Unlimited Autonomous Document Agents",
      "10 Supported Document Languages",
      "250 GB Cloud Storage",
      "5 Team Users & Unlimited Reviewers",
      "Enterprise REST API Access",
      "Premium Reasoning Models",
    ],
  },
];

const Pricing = ({ content, productName }) => {
  const isDocsAI =
    productName?.toLowerCase() === "docsai" ||
    content?.productName?.toLowerCase() === "docsai" ||
    content?.overview?.title?.toLowerCase().includes("docs") ||
    content?.title?.toLowerCase().includes("docs") ||
    (typeof window !== "undefined" && window.location.pathname.toLowerCase().includes("docsai"));

  const plans = isDocsAI ? docsAIPlansData : masterPlansData;

  const headerLabel = isDocsAI ? "DOCS AI PRICING" : "PRICING";
  const headerTitle = isDocsAI
    ? "Predictable Document AI Processing"
    : "Flexible plans built for growth";
  const headerSub = isDocsAI
    ? "Automate document classification, data extraction, and OCR workflows with transparent pricing."
    : "Scale your enterprise AI capabilities with predictable, transparent pricing.";

  return (
    <div className="pricing-wrapper">
      <div className="pricing-section">
        {/* Apple Editorial Header Meta */}
        <div className="pricing-header-meta">
          <span className="pricing-label-apple">{headerLabel}</span>
          <h2 className="pricing-main-title">{headerTitle}</h2>
          <p className="pricing-main-sub">{headerSub}</p>
        </div>

        {/* Apple Spatial Luxury Tier Cards (3 Columns) */}
        <div className="pricing-grid">
          {plans.map((plan) => (
            <div
              key={plan.id || plan.name}
              className={`pricing-card ${plan.popular ? "popular" : ""}`}
            >
              {/* Floating Popular Badge Attached at Top Center */}
              {plan.popular && (
                <div className="pricing-popular-badge">
                  <Star size={12} fill="#F05742" color="#F05742" className="pricing-badge-star" />
                  <span>MOST POPULAR</span>
                </div>
              )}

              {/* Plan Header & Price Staging */}
              <div className="pricing-card-header">
                <span className={`pricing-plan-title ${plan.popular ? "coral" : ""}`}>
                  {plan.name}
                </span>

                <div className="pricing-price-stage">
                  <span className="pricing-amount">${plan.price}</span>
                  <span className="pricing-period">/ month</span>
                </div>

                {plan.conversations && (
                  <div className="pricing-conversation-pill">
                    {plan.conversations}
                  </div>
                )}
              </div>

              {/* Key Metrics Quick Highlights */}
              <div className="pricing-spec-rows">
                {plan.pages && (
                  <div className="pricing-spec-item highlight-spec">
                    <span className="spec-icon-circle">
                      <FileText size={14} strokeWidth={1.8} />
                    </span>
                    <span className="spec-text font-semibold">{plan.pages}</span>
                  </div>
                )}

                <div className="pricing-spec-dual-row">
                  {plan.users && (
                    <div className="pricing-spec-item">
                      <span className="spec-icon-circle">
                        <User size={14} strokeWidth={1.8} />
                      </span>
                      <span className="spec-text font-semibold">{plan.users}</span>
                    </div>
                  )}
                  {plan.agents && (
                    <div className="pricing-spec-item">
                      <span className="spec-icon-circle">
                        <Users size={14} strokeWidth={1.8} />
                      </span>
                      <span className="spec-text font-semibold">{plan.agents}</span>
                    </div>
                  )}
                </div>

                {plan.credits && (
                  <div className="pricing-spec-item highlight-spec">
                    <span className="spec-icon-circle credit-icon">
                      <Sparkles size={14} strokeWidth={1.8} />
                    </span>
                    <span className="spec-text font-semibold">{plan.credits}</span>
                  </div>
                )}

                {plan.reviewers && (
                  <div className="pricing-spec-item">
                    <span className="spec-icon-circle">
                      <Check size={14} strokeWidth={2.4} />
                    </span>
                    <span className="spec-text font-semibold">{plan.reviewers}</span>
                  </div>
                )}

                {plan.api && (
                  <div className="pricing-spec-item">
                    <span className="spec-icon-circle">
                      <Send size={14} strokeWidth={1.8} />
                    </span>
                    <span className="spec-text">{plan.api}</span>
                  </div>
                )}

                {plan.broadcasts && (
                  <div className="pricing-spec-item">
                    <span className="spec-icon-circle">
                      <Send size={14} strokeWidth={1.8} />
                    </span>
                    <span className="spec-text">{plan.broadcasts}</span>
                  </div>
                )}

                {plan.storage && (
                  <div className="pricing-spec-item">
                    <span className="spec-icon-circle">
                      <Database size={14} strokeWidth={1.8} />
                    </span>
                    <span className="spec-text">{plan.storage}</span>
                  </div>
                )}

                {plan.languages && (
                  <div className="pricing-spec-item">
                    <span className="spec-icon-circle">
                      <Globe size={14} strokeWidth={1.8} />
                    </span>
                    <span className="spec-text">{plan.languages}</span>
                  </div>
                )}
              </div>

              {/* Hairline Divider */}
              <div className="pricing-divider" />

              {/* Clean Vertical Single-Column Feature Checklist */}
              <div className="pricing-card-checklist">
                {plan.features.map((feat, idx) => (
                  <div key={idx} className="checklist-item">
                    <span className="checklist-icon-circle">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span className="checklist-text">{feat}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Pricing;